// API configuration for Bearri
interface ApiConfig {
  baseUrl: string;
  endpoints: {
    youtubeChannelSearch: string;
    youtubeDateHelper: string;
    youtubeHealth: string;
    health: string;
    codes: string;
    dataCreators: string;
    dataBrands: string;
    dataStats: string;
    dataProspects: string;
    dataMyProspects: string;
    dataSimilarToBrand: string;
    dataProspectsHistory: string;
    dataProspectRun: string;
    dataPitchHistory: string;
    dataOutreachHints: string;
    dataSeed: string;
    dataPitch: string;
    instagramAnalyze: string;
    instagramProgress: string;
    instagramLookup: string;
  };
}

// Environment configurations
const environments = {
  local: { baseUrl: 'http://localhost:8000' },
  aws:   { baseUrl: 'https://api.bearri.co' },   // production API on ECS Fargate behind ALB
  prod:  { baseUrl: 'https://api.bearri.co' },   // alias
};

// Auto-detect environment
const getEnvironment = (): keyof typeof environments => {
  // 1. Check explicit environment variable first
  if (process.env.REACT_APP_API_ENV) {
    return process.env.REACT_APP_API_ENV as keyof typeof environments;
  }
  
  // 2. Auto-detect based on hostname
  if (typeof window !== 'undefined') {
    const hostname = window.location.hostname;
    if (hostname === 'localhost' || hostname === '127.0.0.1') {
      return 'local';
    }
  }
  
  // 3. Default to AWS for production builds
  return 'aws';
};

const envKey = getEnvironment();

// Create config based on environment
const apiConfig: ApiConfig = {
  baseUrl: environments[envKey]?.baseUrl || environments.aws.baseUrl,
  endpoints: {
    youtubeChannelSearch: '/api/youtube-channel-search',
    youtubeDateHelper: '/api/youtube-date-helper',
    youtubeHealth: '/api/youtube-health',
    health: '/health',
    codes: '/api/codes',
    dataCreators: '/api/data/creators',
    dataBrands: '/api/data/brands',
    dataStats: '/api/data/stats',
    dataProspects: '/api/data/prospects',
    dataMyProspects: '/api/data/my-prospects',
    // Sprint 9 / Track 3 — similar-to-brand mode + run/pitch history
    dataSimilarToBrand: '/api/data/prospects/similar-to-brand',
    dataProspectsHistory: '/api/data/prospects/history',
    dataProspectRun: '/api/data/prospects/runs',  // appended with /{id}
    dataPitchHistory: '/api/data/pitches/history',
    // Sprint 9 — "Find their partnerships team" outreach hints (replaces Hunter.io)
    dataOutreachHints: '/api/data/prospects/outreach-hints',
    dataSeed: '/api/data/seed',
    dataPitch: '/api/data/pitch',
    instagramAnalyze: '/api/instagram/analyze-async',
    instagramProgress: '/api/instagram/progress',
    instagramLookup: '/api/instagram/lookup',
  }
};

if (process.env.NODE_ENV !== 'production') {
  console.log('API Config:', {
    envKey,
    baseUrl: apiConfig.baseUrl,
    autoDetected: !process.env.REACT_APP_API_ENV,
    hostname: typeof window !== 'undefined' ? window.location.hostname : 'server',
    environment: process.env.NODE_ENV,
    REACT_APP_API_ENV: process.env.REACT_APP_API_ENV
  });
}

// Helper function to get base URL
export const getBaseUrl = () => apiConfig.baseUrl;

export default apiConfig; 